import { useQuery } from '@tanstack/react-query';
import getConfig from 'next/config';
import { StoreLocatorPageContentfulResponse, StoreLocatorPageData, ContentSection } from "@/storeLocator/types";
import formatAdvisoryMessageJson from "@/storeLocator/utils/formatAdvisoryMessageJson";
import createStatesListChunks from "@/storeLocator/utils/createStatesListChunks";

const { publicRuntimeConfig } = getConfig();
const { 
  CONTENTFUL_SPACE_ID, 
  CONTENTFUL_ENVIRONMENT, 
  CONTENTFUL_ACCESS_TOKEN, 
  CONTENTFUL_BASE_URL 
} = publicRuntimeConfig;

const fetchStoreLocatorPageData = async (query: string): Promise<StoreLocatorPageData> => {
  const response = await fetch(
    `${CONTENTFUL_BASE_URL}/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query })
    }
  );

  const data: StoreLocatorPageContentfulResponse = await response.json();
  const items = data?.data?.informationalPageCollection?.items?.[0]?.contentSectionsCollection?.items || [];

  return items.reduce((acc: StoreLocatorPageData, section: ContentSection) => {
    switch (section.__typename) {
      case "InformationalPageBannerWithCta":
        acc.heroSection = {
          title: section.title,
          description: section.description,
          buttonText: section.cta.buttonText,
          image: section.responsiveBannerImage.md.url
        };
        break;
      case "InformationPageBannerWithRichText":
        acc.advisoryMessage = {
          title: section.title,
          description: formatAdvisoryMessageJson(section.richTextDescription.json),
          image: section.responsiveBannerImage.md.url
        };
        break;
      case "InformationalPageNavigationSection":
        acc.statesList = {
          title: section.title,
          states: createStatesListChunks(section.storeLocatorPageStates)
        };
        break;
      default:
        console.warn(`Unhandled section type: ${(section as any).__typename}`);
    }
    return acc;
  }, {} as StoreLocatorPageData);
};

export function useStoreLocatorPageData(query: string) {
  return useQuery({
    queryKey: ['storeLocator', query],
    queryFn: () => fetchStoreLocatorPageData(query)
  });
}